import _groupBy from 'lodash/groupBy'
import _isEqual from 'lodash/isEqual'
import {
  addWidgetToOverview, getOverviewWidgetsLayout, removeWidgetFromOverview,
} from "frontend/communications/personOverviewWidgetsLayout"
import { OverviewWidget } from "frontend/communications/dto/PersonOverviewWidgetsLayout"
import { SETTINGS_CARDS_TOKENS } from "frontend/constants/settings"
import { useProfileStore } from 'frontcore/store/useProfileStore'

const stateObj = {
  allowedPerimeterIds: [],
  children: [],
  descendantRoles: [],
  hasAnyApprovalPermission: false,
  id: null,
  isAdministrator: false,
  isOverallManager: false,
  isOverallStandardApproveSupervisor: false,
  isOverallStandardCreateSupervisor: false,
  isProblemPlanificator: false,
  loginType: null,
  managementStructure: [],
  name: '',
  operatorStructure: [],
  parent: null,
  perimeterIdsAsApprovalSupervisor: [],
  perimeterIdsAsCreateSupervisor: [],
  perimeterIdsManaged: [],
  permissions: {},
  personOverviewWidgetsLayout: [],
  problemSupervisor: false,
  role: null,
  roleModules: [],
}

const getters = {
  userName (state) {
    return state.name
  },
  userId (state) {
    return state.id
  },
  isAdministrator (state) {
    return state.isAdministrator
  },
  isModuleManager: (state, globalGetters) => (moduleName) => {
    if (state.isAdministrator) { return true }
    const module = globalGetters.getModulePermissions().find(mod => mod.name == moduleName)
    if (!module) { return false }
    return state.roleModules.some(roleModule => roleModule.active && module.id === roleModule.id)
  },
  managementStructure (state) {
    return state.managementStructure
  },
  perimeterIdsManaged (state) {
    return state.perimeterIdsManaged
  },
  perimeterIdsAsApprovalSupervisor (state) {
    return state.perimeterIdsAsApprovalSupervisor
  },
  perimeterIdsAsCreateSupervisor (state) {
    return state.perimeterIdsAsCreateSupervisor
  },
  getUserPermission (state) {
    return state.permissions
  },
  getAuditModulePermission: (state) => (module) => {
    return state.permission?.observer_overalls.findLast(overall => { return overall.source === module })
  },
  userHasDeletionPermissions: (state) => (perimeterID) => {
    if (
      state.isAdministrator ||
      state.managementStructure.some(s => s.pattern === -1)
    ) { return true }
    return state.perimeterIdsManaged.some(id => id === perimeterID)
  },
  userHasAccessRolePermission (state) {
    return (
      state.role != null
    )
  },
  userHasAccessPermission (state) {
    return (
      state.isAdministrator ||
      state.managementStructure.some(s => s.pattern === -1) ||
      state.perimeterIdsManaged.length > 0 ||
      state.isOverallManager
    )
  },
  userHasAccessToPerimeter: (state) => (perimeterID) => {
    return (
      state.isAdministrator ||
      state.isOverallManager ||
      state.managementStructure.some(s => s.pattern === -1) ||
      state.perimeterIdsManaged.some(id => id === perimeterID)
    )
  },
  overallManager (state) {
    return state.isOverallManager
  },
  overallCreateSupervisor (state) {
    return state.isOverallStandardCreateSupervisor
  },
  overallApproveSupervisor (state) {
    return state.isOverallStandardApproveSupervisor
  },
  isVisualStandardAdmin (state, globalGetters) {
    return globalGetters.isModuleManager(SETTINGS_CARDS_TOKENS.visualStandard.name)
  },
  mpsnowMenuItems (state) {
    return state.mpsnowMenuItems.sort((a, b) => { return a.order - b.order })
  },
  userHasAnyApprovalPermission (state) {
    return state.hasAnyApprovalPermission
  },
  getRole (state) {
    return state.role
  },
  getRoleModules (state) {
    return state.roleModules
  },
  getRoleModulesByType: (state) => (type, include_children=false) => {
    return state.roleModules.filter(perm => (!perm.parent || include_children) && perm.module_type === type)
  },
  getDescendantRoles (state) {
    return state.descendantRoles
  },
  getChildren (state) {
    return state.children
  },
  getManagerId (state) {
    return state.parent
  },
  isPerimeterIdAllowed: (state) => (perimeterId) => {
    return state.allowedPerimeterIds.includes(perimeterId)
  },
  isProblemSupervisor (state) {
    return state.problemSupervisor
  },
  groupedPersonOverviewWidgetsLayout (state) {
    const result = {}
    const moduleGroups = _groupBy(state.personOverviewWidgetsLayout, (layout) => layout.module)
    Object.entries(moduleGroups)
      .sort(([lhsModule], [rhsModule]) => lhsModule - rhsModule)
      .forEach(([module, moduleWidgets]) => {
        const rows = _groupBy(moduleWidgets, (widget) => widget.coordinates.y)
        result[module] = Object.entries(rows).sort(([lhs], [rhs]) => lhs - rhs).map(([, widgets]) => {
          return widgets.sort((lhs, rhs) => lhs.coordinates.x - rhs.coordinates.x)
        })
      })
    return result
  },
  personOverviewWidgetsLayout (state) {
    return state.personOverviewWidgetsLayout
  },
  getModulePinnedWidgets: (state) => (moduleName) => {
    return state.personOverviewWidgetsLayout.filter(widget => widget.module == moduleName)
  },
  isProblemPlanificator (state) {
    return state.isProblemPlanificator
  },
  getUserLoginType (state) {
    return state.loginType
  },
}

const mutations = {
  setName (state, name) {
    state.name = name
  },
  setUserId (state, id) {
    state.id = id
  },
  setParentId (state, parent) {
    state.parent = parent
  },
  setRole (state, role) {
    state.role = role
  },
  setIsAdministrator (state, isAdministrator) {
    state.isAdministrator = isAdministrator
  },
  setManagementStructure (state, managementStructure) {
    state.managementStructure = managementStructure
  },
  setOperatorStructure (state, operatorStructure) {
    state.operatorStructure = operatorStructure
  },
  setPerimeterIdsManaged (state, perimeterIdsManaged) {
    state.perimeterIdsManaged = perimeterIdsManaged
  },
  setPerimeterIdsAsApprovalSupervisor (state, perimeterIdsManaged) {
    state.perimeterIdsAsApprovalSupervisor = perimeterIdsManaged
  },
  setPerimeterIdsAsCreateSupervisor (state, ids) {
    state.perimeterIdsAsCreateSupervisor = ids
  },
  setProblemSupervisor (state, supervisor) {
    state.problemSupervisor = supervisor
  },
  setPermissions (state, profile) {
    state.permission = profile?.permission
    state.isOverallManager = profile?.permission?.is_overall_manager
    state.isOverallStandardApproveSupervisor = profile?.permission?.is_overall_approve_supervisor ?? false
    state.isOverallStandardCreateSupervisor = profile?.permission?.is_overall_create_supervisor ?? false
    state.hasAnyApprovalPermission = state.isOverallStandardApproveSupervisor ||
                                     profile?.perimeters_as_consolidate_supervisor.length > 0
  },
  setProblemPlanificator (state, value) {
    state.isProblemPlanificator = value
  },
  setRoleModules (state, roleModules) {
    state.roleModules = roleModules
  },
  setDescendantRoles (state, descendantRoles) {
    state.descendantRoles = descendantRoles
  },
  setChildren (state, children) {
    state.children = children
  },
  setAllowedPerimeterIds (state, perimeterIds) {
    state.allowedPerimeterIds = perimeterIds
  },
  setUserLoginType (state, loginType) {
    state.loginType = loginType
  },
  saveOverviewWidgetsLayout (state, configs) {
    if (!configs.every(widget => widget instanceof OverviewWidget)) {
      console.error('Widgets must be instance of OverviewWidget')
    }
    state.personOverviewWidgetsLayout = configs
  },
}

const actions = {
  async fetchProfile ({ commit }) {
    const profileStore = useProfileStore()
    const profile = await profileStore.fetch()
    commit('setUserId', profile.id)
    commit('setParentId', profile.parent)
    commit('setName', profile.name)
    commit('setRole', profile.role)
    commit('setIsAdministrator', profile.is_administrator)
    commit('setManagementStructure', profile.management_structure)
    commit('setOperatorStructure', profile.operator_structure)
    commit('setPerimeterIdsManaged', profile.perimeter_ids_managed)
    commit('setPerimeterIdsAsApprovalSupervisor', profile.perimeters_as_consolidate_supervisor)
    commit('setPerimeterIdsAsCreateSupervisor', profile.perimeters_as_create_supervisor)
    commit('setProblemPlanificator', profile.is_problem_planificator)
    commit('setPermissions', profile)
    commit('setRoleModules', profile.role_modules)
    commit('setDescendantRoles', profile.descendant_roles)
    commit('setChildren', profile.children)
    commit('setProblemSupervisor', profile.is_problem_supervisor)
    commit('setAllowedPerimeterIds', profileStore.allManagedPerimeterIds)
    commit('saveOverviewWidgetsLayout', await getOverviewWidgetsLayout())
    commit('setUserLoginType', profile.login_type)
    return profile
  },
  resetProfile ({ commit }) {
    commit('setName', null)
    commit('setRole', null)
    commit('setIsAdministrator', false)
    commit('setManagementStructure', [])
    commit('setOperatorStructure', [])
    commit('setPerimeterIdsManaged', [])
    commit('setRoleModules', [])
    commit('setPermissions', null)
    commit('setUserLoginType', null)
  },
  async addWidgetToLayout ({commit}, {widgetName, module, coordinates, props}) {
    await addWidgetToOverview(widgetName, module, coordinates, props)
    const widgetsLayoutConfig = await getOverviewWidgetsLayout()
    commit('saveOverviewWidgetsLayout', widgetsLayoutConfig)
    return widgetsLayoutConfig
  },
  async removeWidgetFromLayout ({commit}, {widgetName, props}) {
    await removeWidgetFromOverview(widgetName, props)
    const widgetsLayoutConfig = await getOverviewWidgetsLayout()
    commit('saveOverviewWidgetsLayout', widgetsLayoutConfig)
  },
  async removeWidgetFromSetting ({commit}, {widgetName, props}) {
    const widgetsLayoutConfig = await getOverviewWidgetsLayout()
    if (widgetsLayoutConfig.filter(
      (widgetsLayout) => widgetsLayout.widgetName === widgetName && _isEqual(widgetsLayout.props, props)).length > 0) {
      await removeWidgetFromOverview(widgetName, props)
      commit('saveOverviewWidgetsLayout', widgetsLayoutConfig.filter(
        (widgetsLayout) => widgetsLayout.widgetName !== widgetName || !_isEqual(widgetsLayout.props, props)))
    }
  },
  async removeAllSameNameWidgetsFromSetting ( {commit}, widgetName) {
    const widgetsLayoutConfig = await getOverviewWidgetsLayout()
    const widgets = widgetsLayoutConfig.filter((widgetsLayout) => widgetsLayout.widgetName == widgetName)
    const promises = []
    widgets.forEach(widget => promises.push(removeWidgetFromOverview(widget.widgetName, widget.props)))
    Promise.all(promises).then(() => {
      commit('saveOverviewWidgetsLayout', widgetsLayoutConfig.filter((widgetsLayout) => widgetsLayout.widgetName !== widgetName))
    })
  },
}

export default {
  state: stateObj,
  getters,
  mutations,
  actions,
}
